// src/dashboard/Subscription.jsx

import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { AuthContext } from '../context/AuthContext';
import { db } from '../config/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';

/**
 * Subscription
 * Dashboard billing screen using Stripe.
 * - No inline styles. Uses foundation classes (.la)
 * - i18n keys in kebab-case under subscription.* and common.*
 * - Frontend expects backend endpoints:
 *   - POST /api/billing/create-checkout-session  -> { sessionId }
 *   - POST /api/billing/create-portal-session    -> { url }
 *   - GET  /api/billing/current-subscription     -> Stripe-subscription shape (optional)
 * - Fallback read for status at Firestore: users/{uid}/billing/status
 */

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

export default function Subscription() {
  const intl = useIntl();
  const { user, token } = useContext(AuthContext); // token opcional para backend auth

  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [sub, setSub] = useState(null); // current subscription payload
  const [customerEmail, setCustomerEmail] = useState('');

  const isSubscribed = useMemo(() => !!sub && sub.status && sub.status !== 'incomplete' && sub.status !== 'canceled', [sub]);
  const planName = useMemo(() => sub?.items?.[0]?.price?.nickname || sub?.plan?.nickname || '—', [sub]);
  const renewsAt = useMemo(() => {
    const ts = sub?.current_period_end;
    if (!ts) return null;
    const dt = typeof ts === 'number' ? new Date(ts * 1000) : ts.toDate?.() || null;
    return dt ? dt.toISOString().split('T')[0] : null;
  }, [sub]);

  // -------- Helpers --------
  async function fetchJSON(url, opts = {}) {
    const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { ...opts, headers });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
  }

  // -------- Load current subscription --------
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user?.uid) return;
      try {
        setLoading(true);
        // Prefer backend authoritative data (Stripe API)
        try {
          const api = await fetchJSON('/api/billing/current-subscription');
          if (mounted && api) {
            setSub(api.subscription || api);
            setCustomerEmail(api.customerEmail || user.email || '');
          }
        } catch (e) {
          // Fallback to Firestore mirror if API not available
          const ref = doc(db, 'users', user.uid, 'billing', 'status');
          const snap = await getDoc(ref);
          if (mounted && snap.exists()) {
            const data = snap.data();
            setSub(data.subscription || null);
            setCustomerEmail(data.customerEmail || user.email || '');
          }
        }
      } catch (err) {
        console.error('Subscription: load error', err);
        toast.error(intl.formatMessage({ id: 'toast.load-error' }));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [user?.uid]);

  // -------- Actions --------
  const handleCheckout = async () => {
    try {
      setWorking(true);
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe not initialized');
      const payload = await fetchJSON('/api/billing/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({ uid: user?.uid }),
      });
      if (!payload?.sessionId) throw new Error('Missing sessionId');
      const { error } = await stripe.redirectToCheckout({ sessionId: payload.sessionId });
      if (error) throw error;
    } catch (err) {
      console.error('Subscription: checkout error', err);
      toast.error(intl.formatMessage({ id: 'subscription.checkout-error' }));
    } finally {
      setWorking(false);
    }
  };

  const handlePortal = async () => {
    try {
      setWorking(true);
      const payload = await fetchJSON('/api/billing/create-portal-session', {
        method: 'POST',
        body: JSON.stringify({ uid: user?.uid, return_url: window.location.href }),
      });
      if (!payload?.url) throw new Error('Missing portal url');
      window.location.href = payload.url;
    } catch (err) {
      console.error('Subscription: portal error', err);
      toast.error(intl.formatMessage({ id: 'subscription.portal-error' }));
    } finally {
      setWorking(false);
    }
  };

  // -------- UI --------
  return (
    <div className="la dashboard-page">
      <div className="page-header">
        <h1 className="page-title">{intl.formatMessage({ id: 'subscription.title', defaultMessage: 'Subscription' })}</h1>
        <p className="page-subtitle">{intl.formatMessage({ id: 'subscription.subtitle', defaultMessage: 'Manage your plan, billing and invoices.' })}</p>
      </div>

      <Elements stripe={stripePromise}>
        <section className="card">
          <div className="card-header">
            <h2 className="card-title">{intl.formatMessage({ id: 'subscription.current-plan', defaultMessage: 'Current plan' })}</h2>
            <p className="card-subtitle">{intl.formatMessage({ id: 'subscription.current-plan.help', defaultMessage: 'Plan details synced with Stripe.' })}</p>
          </div>
          <div className="card-content">
            {loading ? (
              <div className="loading-overlay">
                <div className="spinner" aria-label={intl.formatMessage({ id: 'common.loading', defaultMessage: 'Loading' })} />
              </div>
            ) : (
              <div className="plan-summary">
                <div className="plan-grid">
                  <div className="plan-field">
                    <div className="label">{intl.formatMessage({ id: 'subscription.email', defaultMessage: 'Billing email' })}</div>
                    <div className="value">{customerEmail || '—'}</div>
                  </div>
                  <div className="plan-field">
                    <div className="label">{intl.formatMessage({ id: 'subscription.status', defaultMessage: 'Status' })}</div>
                    <div className={`badge ${isSubscribed ? 'success' : 'muted'}`}>{sub?.status || '—'}</div>
                  </div>
                  <div className="plan-field">
                    <div className="label">{intl.formatMessage({ id: 'subscription.plan', defaultMessage: 'Plan' })}</div>
                    <div className="value">{planName}</div>
                  </div>
                  <div className="plan-field">
                    <div className="label">{intl.formatMessage({ id: 'subscription.renews', defaultMessage: 'Renews at' })}</div>
                    <div className="value">{renewsAt || '—'}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="card-footer actions">
            <button className="btn primary" onClick={handleCheckout} disabled={working}>
              {intl.formatMessage({ id: 'subscription.change-plan', defaultMessage: 'Change / Subscribe' })}
            </button>
            <button className="btn" onClick={handlePortal} disabled={working}>
              {intl.formatMessage({ id: 'subscription.open-portal', defaultMessage: 'Open Customer Portal' })}
            </button>
          </div>
        </section>

        <section className="card">
          <div className="card-header">
            <h2 className="card-title">{intl.formatMessage({ id: 'subscription.invoices', defaultMessage: 'Invoices' })}</h2>
            <p className="card-subtitle">{intl.formatMessage({ id: 'subscription.invoices.help', defaultMessage: 'You can download your invoices from the portal.' })}</p>
          </div>
          <div className="card-content">
            <div className="empty-state">
              <div className="empty-title">{intl.formatMessage({ id: 'subscription.invoices.empty.title', defaultMessage: 'No invoices here' })}</div>
              <div className="empty-desc">{intl.formatMessage({ id: 'subscription.invoices.empty.desc', defaultMessage: 'Access the customer portal to see your billing history.' })}</div>
              <div className="empty-actions">
                <button className="btn" onClick={handlePortal} disabled={working}>
                  {intl.formatMessage({ id: 'subscription.open-portal', defaultMessage: 'Open Customer Portal' })}
                </button>
              </div>
            </div>
          </div>
        </section>
      </Elements>
    </div>
  );
}
